import { useEffect } from 'react';
import { connect } from 'react-redux';
import { rulesOfBehaviorIntro } from '@gonfalon/bootstrap-data/bootstrapData';
import { disallowSignups, federalCssStylesEnabled as federalCssStyles } from '@gonfalon/constants/constants';
import { areSignupsDisallowed } from '@gonfalon/dogfood-flags/dogfood-flags';
import { getSession } from '@gonfalon/rest-api/queries/session';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';
import { createSelector } from 'reselect';

import { createUseMFARecoveryAction, recoverMFA, verifyMFA } from '#actions/auth';
import { resetTimeToLoginMetrics, setTimeToLoginStartTime } from '#actions/timeToLoginMetric';
import type { LoginProps } from '#components/loginAndSignUp/Login';
import { Login } from '#components/loginAndSignUp/Login';
import { useDispatch } from '#hooks/useDispatch';
import { useSelector } from '#hooks/useSelector';
import { loginSelector } from '#reducers/auth';
import { selectTimeToLoginMetrics } from '#reducers/timeToLoginMetric';
import { trackSignInPageLoaded } from '#utils/loginUtils';
import { shouldEnableGithubOAuthSignUp, shouldEnableGoogleOAuthSignUp } from '#utils/oauthUtils';
import { firstStringFromQueryString } from '#utils/qsUtils';
import { DEFAULT_REDIRECT_PATH, getIsValidRedirectPath } from '#utils/urlUtils';

const useRedux = () => {
  const dispatch = useDispatch();
  const timeToLoginMetrics = useSelector(selectTimeToLoginMetrics);

  const dispatchResetTimeToLoginMetrics = () => dispatch(resetTimeToLoginMetrics());
  const setTimeToLoginStartTimeMetric = (startTime: number) => dispatch(setTimeToLoginStartTime(startTime));

  return {
    dispatchResetTimeToLoginMetrics,
    setTimeToLoginStartTimeMetric,
    timeToLoginMetrics,
  };
};

type LoginContainerProps = LoginProps & {
  isSSOConfigured?: boolean;
};

const BaseLoginContainer = (props: LoginContainerProps) => {
  const { dispatchResetTimeToLoginMetrics, setTimeToLoginStartTimeMetric } = useRedux();
  const { redirect = DEFAULT_REDIRECT_PATH, requirePassword, beastMode, isSSOConfigured } = props;
  const { data } = useQuery({
    ...getSession({}),
    // Customers may have multiple tabs open, we want to refetch the session when the user focuses on the tab
    refetchOnWindowFocus: true,
    retry: false,
  });

  useEffect(() => {
    if (data && !beastMode) {
      // The login service sends an OAuth-only member here to re-authenticate with their
      // provider (204 with Location: /login, which the SSO redirect helper stamps with a
      // RelayState param — nothing else links to /login with one). That member may hold a
      // session for a *different* organization, so redirecting to it would hide the
      // provider buttons they were sent here to click.
      if (new URLSearchParams(window.location.search).has('RelayState')) {
        return;
      }
      if (getIsValidRedirectPath(redirect)) {
        window.location.assign(redirect);
      } else {
        window.location.assign(DEFAULT_REDIRECT_PATH);
      }
    }
  }, [data, redirect, beastMode]);

  useEffect(() => {
    trackSignInPageLoaded();
    dispatchResetTimeToLoginMetrics();
    setTimeToLoginStartTimeMetric(Date.now());
  }, []);

  return <Login {...props} requirePassword={requirePassword || !!(beastMode && !isSSOConfigured)} />;
};

const mapStateToProps = createSelector(loginSelector, (loginState) => {
  const { token, error, needsRefresh, requirePassword, isRecovery } = loginState;
  // `showPassword` is a hack to enable Calibre to log in programmatically
  const queryParams = qs.parse(window.location.search.slice(1));
  const forcePassword = !!queryParams.showPassword;
  const googleOauthError = !!queryParams.googleOauthError;
  const githubOauthError = !!queryParams.githubOauthError;
  const vercelOauthError = !!queryParams.vercelOauthError;
  const redirect = firstStringFromQueryString(queryParams.redirect);

  return {
    token,
    error,
    githubOauthError,
    googleOauthError,
    vercelOauthError,
    needsRefresh,
    requirePassword: forcePassword || requirePassword,
    redirect,
    isRecovery,
    rulesOfBehaviorIntro: rulesOfBehaviorIntro(),
    disallowSignups: disallowSignups() || areSignupsDisallowed(),
    enableGoogleOAuthSignUp: shouldEnableGoogleOAuthSignUp(),
    enableGithubOAuthSignUp: shouldEnableGithubOAuthSignUp(),
    federalCssStylesEnabled: federalCssStyles(),
  };
});

const mapDispatchToProps = {
  onMFAVerify: verifyMFA,
  onMFARecover: recoverMFA,
  onUseMFARecovery: createUseMFARecoveryAction,
};

export const LoginContainer = connect(mapStateToProps, mapDispatchToProps)(BaseLoginContainer);
